// src/components/Landscapes.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import "./photographs.css";
import AudioFloat from "./AudioFloat.js";

/* -------------------------------------
   Body data attribute (light nav mode)
------------------------------------- */
function useLightNav(flag = true) {
  useEffect(() => {
    const el = document?.body;
    if (!el) return;
    if (flag) el.setAttribute("data-light-nav", "true");
    return () => el.removeAttribute("data-light-nav");
  }, [flag]);
}

/* -------------------------------------
   Detect mobile vs desktop
------------------------------------- */
function useIsMobile(query = "(max-width: 991px)") {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const on = (e) => setIsMobile(e.matches);

    mql.addEventListener
      ? mql.addEventListener("change", on)
      : mql.addListener(on);

    return () => {
      mql.removeEventListener
        ? mql.removeEventListener("change", on)
        : mql.removeListener(on);
    };
  }, [query]);

  return isMobile;
}

/* -------------------------------------
   Reveal on scroll
------------------------------------- */
function useRevealOnScroll(deps = []) {
  useEffect(() => {
    const nodes = document.querySelectorAll(".photographs-page .reveal");
    if (!nodes.length) return;

    if (!("IntersectionObserver" in window)) {
      nodes.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "10% 0px -4% 0px" }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, deps);
}

/* -------------------------------------
   Helpers
------------------------------------- */
function getOrder(p) {
  const raw =
    (p?.order && (p.order.$numberInt ?? p.order.$numberLong)) ??
    p?.order ??
    9_999_999;

  if (typeof raw === "number") return raw;

  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 9_999_999;
  }

  return 9_999_999;
}

/* -------------------------------------
   Component
------------------------------------- */
export default function Landscapes({ fetchPhotographs, photographs }) {
  useLightNav(true);
  const isMobile = useIsMobile();

  // we'll still track load count in case you want future auto-behavior,
  // but we are NOT using it for any gate now
  const [loadedCount, setLoadedCount] = useState(0);

  /* ---------- grid prefs ---------- */
  const [mobileCols, setMobileCols] = useState(() => {
    try {
      return Number(localStorage.getItem("lvl_photos_mobile_cols")) || 1;
    } catch {
      return 1;
    }
  });

  const [desktopCols, setDesktopCols] = useState(() => {
    try {
      return Number(localStorage.getItem("lvl_photos_desktop_cols")) || 3;
    } catch {
      return 3;
    }
  });

  const [framed, setFramed] = useState(() => {
    try {
      return localStorage.getItem("lvl_grid_frame") === "1";
    } catch {
      return false;
    }
  });

  const [matSize, setMatSize] = useState(() => {
    try {
      return localStorage.getItem("lvl_grid_mat") || "standard";
    } catch {
      return "standard";
    }
  }); // "standard" | "large"

  /* ---------- persist prefs ---------- */
  useEffect(() => {
    try {
      localStorage.setItem("lvl_photos_mobile_cols", String(mobileCols));
    } catch {}
  }, [mobileCols]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_photos_desktop_cols", String(desktopCols));
    } catch {}
  }, [desktopCols]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_grid_frame", framed ? "1" : "0");
    } catch {}
  }, [framed]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_grid_mat", matSize);
    } catch {}
  }, [matSize]);

  /* ---------- fetch data on mount ---------- */
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof fetchPhotographs === "function") {
      fetchPhotographs();
    }
  }, [fetchPhotographs]);

  /* ---------- normalize + FILTER + SORT ---------- */
  const list = useMemo(() => {
    if (!photographs) return [];
    const arr = Array.isArray(photographs)
      ? photographs
      : Object.values(photographs);

    const filtered = arr.filter((p) => {
      if (!p) return false;
      if (!p.imageURL) return false;

      const {
        wildlife,
        landscape,
        print,
        childlabor,
        causes,
        antarctica,
        journey,
        vessel,
        fading,
      } = p;

      // this page is "Landscapes & Wildlife"
      if (!(landscape || wildlife)) return false;

      // exclude other special buckets
      if (print || childlabor || causes) return false;
      if (antarctica || journey || vessel || fading) return false;

      return true;
    });

    return filtered.sort((a, b) => getOrder(a) - getOrder(b));
  }, [photographs]);

  /* ---------- scroll reveal ---------- */
  useRevealOnScroll([
    list,
    mobileCols,
    desktopCols,
    framed,
    matSize,
    isMobile,
  ]);

  /* ---------- per-image load ---------- */
  const handleImgLoad = useCallback((e) => {
    const img = e.currentTarget;
    img.classList.add("is-loaded");

    const card = img.closest(".ph-card");
    const slot = img.closest(".card-link");

    if (card) {
      const { naturalWidth: w, naturalHeight: h } = img;
      const isLandscape = w >= h;

      if (slot) {
        slot.classList.toggle("is-landscape", isLandscape);
        slot.classList.toggle("is-portrait", !isLandscape);
        slot.setAttribute(
          "data-shape",
          isLandscape ? "landscape" : "portrait"
        );
      }

      card.classList.toggle("is-landscape", isLandscape);
      card.classList.toggle("is-portrait", !isLandscape);
      card.setAttribute(
        "data-shape",
        isLandscape ? "landscape" : "portrait"
      );
    }

    setLoadedCount((c) => c + 1);
  }, []);

  /* ---------- classes ---------- */
  const gridClasses = [
    "photographs-grid",
    isMobile
      ? mobileCols === 2
        ? "m-two"
        : "m-one"
      : desktopCols === 2
      ? "d-two"
      : "d-three",
    framed ? "framed-on" : "framed-off",
    framed ? (matSize === "large" ? "mat-large" : "mat-standard") : "",
  ]
    .filter(Boolean)
    .join(" ");

  // match Photographs.jsx: always treat page as 'ready'
  const rootClass = "photographs-page page-is-ready";

  return (
    <div className={rootClass}>
      {/* MAIN CONTENT */}
      <div className="main-page page-pad smaller black">
        {/* Title / intro copy */}
        <header className="page-head reveal">
          <h1 className="page-title-centered">Landscapes & Wildlife</h1>

          <p className="centered-sub">
            My animal portraits are not about wildlife alone — they are about
            presence. Every creature carries its own poetry, its own rhythm of
            survival, silence, and grace. Through their eyes and gestures, I
            find the same truth that I seek in humanity — the quiet strength,
            the fragility, the unspoken emotions that connect all living
            beings.
            <br />
            <br />
            These works are not separated from my human series; they belong to
            the same continuum — a dialogue between man and nature, fragility
            and resilience, voice and stillness.
          </p>

          <p className="centered-sub">
            Tap any piece to view scale, provenance, and acquisition details.
          </p>
        </header>

        {/* Controls */}
        <div
          className="photographs-controls reveal"
          role="group"
          aria-label="View and frame controls"
        >
          {/* View density / columns */}
          {isMobile ? (
            <div className="view" role="group" aria-label="Mobile view density">
              <span className="view-label">View</span>
              <button
                type="button"
                onClick={() => setMobileCols(1)}
                className={
                  mobileCols === 1 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={mobileCols === 1}
              >
                Single
              </button>
              <button
                type="button"
                onClick={() => setMobileCols(2)}
                className={
                  mobileCols === 2 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={mobileCols === 2}
              >
                Duo
              </button>
            </div>
          ) : (
            <div
              className="view"
              role="group"
              aria-label="Desktop view density"
            >
              <span className="view-label">View</span>
              <button
                type="button"
                onClick={() => setDesktopCols(2)}
                className={
                  desktopCols === 2 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={desktopCols === 2}
              >
                Duo
              </button>
              <button
                type="button"
                onClick={() => setDesktopCols(3)}
                className={
                  desktopCols === 3 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={desktopCols === 3}
              >
                Trio
              </button>
            </div>
          )}

          {/* Frame toggle */}
          <label
            className="frame-toggle"
            aria-label="Framing preview toggle"
          >
            <input
              type="checkbox"
              checked={framed}
              onChange={(e) => {
                const next = e.target.checked;
                setFramed(next);
              }}
            />
            <span>Framing Preview</span>
          </label>

          {/* Mat size toggle — only if framed */}
          {framed && (
            <div
              className="mat-toggle"
              aria-label="Matting size options"
            >
              <span className="view-label">Matting</span>
              <button
                type="button"
                onClick={() => setMatSize("standard")}
                className={
                  matSize === "standard"
                    ? "view-spn selected-btn"
                    : "view-spn"
                }
                aria-pressed={matSize === "standard"}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={() => setMatSize("large")}
                className={
                  matSize === "large" ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={matSize === "large"}
              >
                Gallery
              </button>
            </div>
          )}
        </div>

        {/* GRID */}
        <div className={gridClasses}>
          {list.length === 0 ? (
            <div
              className="smaller uppercase meta"
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                opacity: 0.7,
              }}
            >
              No photographs found.
            </div>
          ) : (
            list.map((p) => {
              const { _id, imageURL, title, series, location, year } = p || {};
              const key = _id || imageURL;
              if (!imageURL) return null;

              return (
                <Link
                  to={`/photographs/${_id}`}
                  key={key}
                  className="card-link reveal"
                  data-shape=""
                >
                  <article
                    className={[
                      "ph-card",
                      framed ? "has-frame" : "",
                    ].join(" ")}
                  >
                    <div className="ph-media">
                      <div className="ph-media-inner">
                        <img
                          className="ph-img"
                          src={imageURL}
                          alt={title || "Landscape"}
                          loading="lazy"
                          decoding="async"
                          onLoad={handleImgLoad}
                          onError={handleImgLoad}
                        />
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="ph-meta">
                      {series ? (
                        <div className="ph-series text-gray">{series}</div>
                      ) : null}
                      {title ? (
                        <div className="ph-title-case">{title}</div>
                      ) : null}
                      <div className="ph-lines">
                        {location ? (
                          <div className="ph-location text-gray-2">
                            {location}
                          </div>
                        ) : null}
                        {year ? (
                          <div className="ph-year italic">{year}</div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })
          )}
        </div>
      </div>

      {/* AudioFloat always rendered now */}
      <AudioFloat
        srcs={[
          {
            src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/shop.mp3",
            type: "audio/mpeg",
          },
        ]}
        startMuted={false}
        startVolume={0.5}
        loop={true}
        label="Site background audio"
        startTime={190}
      />
    </div>
  );
}
